import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type WeightMeasuredJournalEvent from "./weight-measured-journal-event";

//---- Automatically generated, don't update this part ----

/**
 * Represents a consultation report associated to the LGC feature.
 */
export default class LgcConsultationReportFilledJournalEvent extends JournalEvent {
  static override type = "lgc-consultation-report-filled-journal-event";

  @attr("string") declare title: string;

  /**
   * The template used for this consultation report (Consultatio, HDJ, RCP).
   */
  @attr("string") declare template: string;

  /**
   * The sub-template of the consultation (suivie, proctologie).
   */
  @attr("string") declare subTemplate: string;

  /**
   * The disease type related to this consultation report (Crohn, RCH, non-mici).
   */
  @attr("string") declare diseaseType: string;

  @attr("string") declare rcpType: string;

  @attr("string") declare consultationType: string;

  @attr("string") declare intercurrentEvents: string;

  @attr("string") declare conclusionGeneral: string;

  @attr("string") declare privateNote: string;

  @attr("boolean", {
    allowNull: true
  })
  declare isDraft: boolean;

  @attr("string") declare informations: string;

  @attr("string") declare overallFeeling: string;

  @attr("string") declare abdominalPain: string;

  @attr("number") declare dailyFeces: number;

  @attr("boolean", {
    allowNull: true
  })
  declare fever: boolean;

  @attr("string") declare diseaseObservations: string;

  @attr("number") declare surveyIbdDisk: number;

  @attr("number") declare treatmentCompliance: number;

  @attr("string") declare treatmentComplianceComment: string;

  @attr("boolean", {
    allowNull: true
  })
  declare hasTreatmentsModification: boolean;

  @attr("string") declare treatmentsModification: string;

  @attr("string") declare otherAdditionalExaminations: string;

  @attr("string") declare generalRecommendations: string;

  @attr("boolean", {
    allowNull: true
  })
  declare stoma: boolean;

  @attr("string") declare abdominalMass: string;

  @attr("string") declare degreeOfUrgency: string;

  @attr("boolean", {
    allowNull: true
  })
  declare anoPeritonealFistula: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare seton: boolean;

  @attr("string") declare overnightFeces: string;

  @attr("string") declare rectalBleeding: string;

  @attr("boolean", {
    allowNull: true
  })
  declare fakeNeed: boolean;

  @attr("string") declare defecateEmergency: string;

  @attr("string") declare activityOverwallFeeling: string;

  @attr("number") declare usualFeces: number;

  @attr("string") declare diseaseActivity: string;

  @attr("string") declare dieticianConsultation: string;

  @attr("string") declare psychologistConsultation: string;

  @attr("string") declare therapeuticEducation: string;

  @attr("string") declare consultationComments: string;

  @attr("string") declare hdjTreatmentDosage: string;

  @attr("date") declare nextHdjDate: Date;

  @attr("string") declare clinicalExaminationSymptoms: string;

  @attr("string") declare treatmentsPrescriptions: string;

  @attr("string") declare conclusionPrescriptions: string;

  @attr("string") declare conclusionPrescriptionsDelivered: string;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.lgc-consultation-report-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      dailyFeces: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.lgc-consultation-report-filled-journal-event.daily-feces"
        )
      }),
      surveyIbdDisk: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.lgc-consultation-report-filled-journal-event.survey-ibd-disk"
        )
      }),
      treatmentCompliance: validateNumber({
        allowBlank: true,
        integer: true,
        gte: 0,
        lte: 100,
        description: intl.t(
          "validations.lgc-consultation-report-filled-journal-event.treatment-compliance"
        )
      }),
      usualFeces: validateNumber({
        allowBlank: true,
        integer: true,
        gte: 0,
        description: intl.t(
          "validations.lgc-consultation-report-filled-journal-event.usual-feces"
        )
      })
    };
  }

  //---- End of generated code ----

  @attr() declare weight: number;

  @belongsTo("patient", {
    async: false,
    inverse: "lgcConsultationReportFilledJournalEvents"
  })
  declare patient: Patient;

  @belongsTo("weight-measured-journal-event", {
    async: false,
    inverse: null
  })
  declare weightMeasuredJournalEvent: WeightMeasuredJournalEvent;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "lgc-consultation-report-filled-journal-event": LgcConsultationReportFilledJournalEvent;
  }
}
